// src/lib/normalize-choices.ts
//
// The render-time bridge from the canonical STORAGE shape of
// `Question.choices` (an array of {label, text} objects) to the
// canonical RENDER shape (string[]) that the SAT runner and
// /review page expect.
//
// CANONICAL STORAGE SHAPE (see prisma/schema.prisma > Question.choices
// and prisma/SAT_QUESTION_GENERATION_INSTRUCTIONS.md):
//
//   Array<{label: string; text: string}>   for MCQ items, OR
//   []                                     for SPR items
//
// Audit during PR #483 confirmed every batch file in
// prisma/sat-questions/ writes this shape, and a test pin in
// __tests__/sat-questions-bank-shape.test.ts now enforces it
// against future content edits. The diagnostic-test items in
// src/data/diagnostic.ts use a STRING shape, but those live in a
// static file and never touch Question.choices.
//
// Why a bridge instead of "just render the objects":
// Prisma's `choices: Json` can't express the shape constraint
// structurally, so we treat the payload as untrusted at the
// render layer. The helper is also defensive — if a one-off bad
// row (e.g. legacy data, a hand-edited row) slips through with
// a different shape, the helper still produces a renderable
// string instead of crashing the page. See the coerceChoiceToString
// fallbacks below.
//
// History: this used to be framed as a defensive "drift coercion"
// against multiple storage shapes coexisting. The audit found the
// bank is uniformly {label, text} — there's no drift to defend
// against, there's just a shape mismatch between storage and
// render that this module reconciles.

/**
 * Canonical storage shape for a single MCQ choice on Question.choices.
 *
 * Use this type from content code (seeds, LLM-generation, audit tools)
 * so the TypeScript compiler catches drift before bad data hits the DB.
 * The Prisma `Json` column can't express this structurally — this is
 * the documented contract, enforced via the test pin in
 * __tests__/sat-questions-bank-shape.test.ts and the audit tool.
 *
 *   { label: 'A', text: '5' }
 *
 * Labels MUST be 'A' | 'B' | 'C' | 'D' and MUST match the choice's
 * position in the array (index 0 → A, etc.). Same convention as the
 * Digital SAT itself.
 */
export type StoredChoice = {
  label: 'A' | 'B' | 'C' | 'D'
  text: string
}

/**
 * Type guard for StoredChoice. Useful for filtering / asserting in
 * content tooling without re-implementing the shape check.
 */
export function isStoredChoice(c: unknown): c is StoredChoice {
  if (!c || typeof c !== 'object') return false
  const obj = c as Record<string, unknown>
  if (typeof obj.text !== 'string') return false
  if (typeof obj.label !== 'string') return false
  return obj.label === 'A' || obj.label === 'B' || obj.label === 'C' || obj.label === 'D'
}

/**
 * Coerce a single raw choice value into a string suitable for JSX.
 *
 *   - string                            → passthrough
 *   - number / boolean                  → String(value)
 *   - object with `text` (string)       → use the text field
 *                                         (the canonical storage shape)
 *   - object with `label` (string) only → use the label
 *   - other object                      → JSON.stringify (best-effort)
 *   - null / undefined                  → ''
 *
 * The first two branches (string, number/boolean) exist as a defense
 * for the static-diagnostic data path (src/data/diagnostic.ts uses
 * plain strings) and for any out-of-band/legacy data. The third branch
 * is the production path for everything in the Question table.
 */
export function coerceChoiceToString(c: unknown): string {
  if (typeof c === 'string') return c
  if (typeof c === 'number' || typeof c === 'boolean') return String(c)
  if (c == null) return ''
  if (typeof c === 'object') {
    const obj = c as Record<string, unknown>
    if (typeof obj.text === 'string') return obj.text
    if (typeof obj.label === 'string') return obj.label
    try {
      return JSON.stringify(c)
    } catch {
      return ''
    }
  }
  return ''
}

/**
 * Apply coerceChoiceToString across an array. Returns an empty array
 * if the input isn't an array (so SPR items / malformed rows give a
 * safe fallback rather than throwing).
 */
export function normalizeChoices(c: unknown): string[] {
  if (!Array.isArray(c)) return []
  return c.map(coerceChoiceToString)
}

/**
 * Whether the original choice value was "richer" than a plain string —
 * i.e. we had to coerce something to render it. Used by the /review
 * boundary so we can keep logging payload-health drift to Sentry as
 * part of the existing "coerced fields" breadcrumb.
 *
 * Anything that wasn't already a string is considered coerced.
 */
export function wasChoiceCoerced(c: unknown): boolean {
  return typeof c !== 'string'
}

/**
 * Short label for the original shape of a choice value, used in
 * Sentry breadcrumbs so a triage engineer can tell whether the
 * upstream DB column is storing strings, {text,label} objects,
 * arrays, etc.
 *
 *   string      → 'string'
 *   number      → 'number'
 *   boolean     → 'boolean'
 *   null/undef  → 'null'
 *   array       → 'array'
 *   object      → 'object:<inner-shape>' where inner-shape is one of
 *                 'text+label', 'text', 'label', 'other'
 *   anything else → typeof
 */
export function describeChoiceShape(c: unknown): string {
  if (c == null) return 'null'
  if (typeof c === 'string') return 'string'
  if (typeof c === 'number') return 'number'
  if (typeof c === 'boolean') return 'boolean'
  if (Array.isArray(c)) return 'array'
  if (typeof c === 'object') {
    const obj = c as Record<string, unknown>
    const hasText = typeof obj.text === 'string'
    const hasLabel = typeof obj.label === 'string'
    if (hasText && hasLabel) return 'object:text+label'
    if (hasText) return 'object:text'
    if (hasLabel) return 'object:label'
    return 'object:other'
  }
  return typeof c
}
